import React, { useContext, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { store } from 'components/Store';
import Share from 'components/Dashboard/components/Share';
import { contentItemSearchQuery } from 'components/Dashboard/helpers/locationSearchDescriptor';
import useRememberPosition from 'components/Dashboard/hooks/useRememberPosition';
import TagsLists from 'components/Dashboard/TagsList';
import calculateDifferenceBetweenDays from 'components/Dashboard/utilities/calculateDifferenceBetweenDays';
import getSearchWithinHash from 'components/Dashboard/utilities/getSearchWithinHash';
import normalizeString from 'components/Dashboard/utilities/normalizeString';
import thousandCommas from 'components/Dashboard/helpers/thousandCommas';
import GeneralItemActions from 'components/Dashboard/ItemController/GeneralItemActions';
import ContentTypeBadge from 'components/Dashboard/components/ContentTypeBadge';
import PriorityBadge from 'components/Dashboard/ItemController/PriorityBadge';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import time from 'utils/time';
import {
  ItemControllerActions,
  ListItemContainer,
  ListItemLink,
} from 'components/Dashboard/ListControl/ListItemLayout';
import useListItemController from 'components/Dashboard/ListControl/useListItemController';
import preventLink from 'components/Dashboard/TagsList/prevenLink';
const ConsultationItem = ({ listRow }) => {
  const {
    listContext = {},
    feedOverrides = {},
    display = {},
    interactions = {},
    external = {},
    sectionMeta = {},
  } = listRow ?? {};
  const { item, index, array, scrollBarRef, zIndex } = listContext;
  const { explanations: explanationsOverride, feedItemFlags: propsFeedItemFlags } = feedOverrides;
  const { notShowShare, hideYellowDot, showContentTypeBadge, tagsFixedPosition, onlyEmbargoedTag, tooltipText } =
    display;
  const { itemOnclick, thumbDownProps, itemActionsProps, showPriorityBadge, setActiveUpdate } = interactions;
  const { embedShowTags } = external;
  const { isAll, includeTags } = sectionMeta;

  const globalState = useContext(store);
  const { state } = globalState;
  const { search } = state;
  const { contentResults } = useContentResults();
  const { id, consultations = [], isCommittee, title, departmentName, openDate } = item ?? {};
  const { activeSearch, list } = search;

  const ConsultationSearch = contentResults['Consultations'];
  const explanations =
    explanationsOverride ?? (activeSearch && ConsultationSearch ? ConsultationSearch.explanations : []);

  const feedItemFlags =
    propsFeedItemFlags ?? (activeSearch && ConsultationSearch ? ConsultationSearch.feedItemFlags : []);

  const explanationsToRender = explanations ?? ConsultationSearch?.explanations;
  const { rememberPosition } = useRememberPosition();

  const decodeString = (string) => {
    const uri_enc = encodeURIComponent(string);
    const uri_dec = decodeURIComponent(uri_enc);
    return uri_dec;
  };

  const { unseenConditional, locationDescriptor, priorityBadgeCondition, rateUpdatesMode } = useListItemController({
    item,
    index,
    feedItemFlags: propsFeedItemFlags,
    hideYellowDot,
    thumbDownProps,
    showPriorityBadge,
  });

  const consultationItemContainer = useRef(null);
  const consultationItemTitle = useRef(null);
  const openCloseItem = (e) => {
    let element = consultationItemTitle.current;
    let targetElement = consultationItemContainer.current;
    let expanded = element.getAttribute('aria-expanded');
    targetElement.classList.remove('collapse');
    if (expanded === 'true') {
      targetElement.classList.add('overflow-hidden');
      targetElement.classList.remove('show');
      targetElement.style.height = '0px';
    } else {
      element.classList.remove('collapsed');
      targetElement.classList.add('collapsing');
      targetElement.style.height = `${targetElement.scrollHeight}px`;
    }
    setTimeout(() => {
      targetElement.classList.add('collapse');
      targetElement.classList.remove('collapsing');
      if (expanded === 'true') {
        element.classList.add('collapsed');
        element.setAttribute('aria-expanded', 'false');
        targetElement.classList.remove('overflow-hidden');
      } else {
        targetElement.classList.add('show');
        element.setAttribute('aria-expanded', 'true');
      }
    }, 350);
  };

  return (
    <>
      {isAll && (
        <div key={id} className='open-consultations-item my-2'>
          <div
            className={`heading p-4 heading-all-open-consultations collapsed`}
            ref={consultationItemTitle}
            aria-expanded={'false'}
            onClick={openCloseItem}
          >
            <h4 className='mb-2 pr-4 pr-sm-0'>{`${decodeString(title)}`}</h4>
            <p className='mb-0'>
              {consultations.length} open{' '}
              {consultations.length === 1
                ? `${isCommittee ? 'enquiry' : 'consultation'}`
                : `${isCommittee ? 'enquiries' : 'consultations'}`}
            </p>
            <img
              className='arrow-down'
              src={`${import.meta.env.VITE_APP_CDNURL}/images/arrow-down.svg`}
              alt='arrow down'
            />
          </div>

          <div className={`collapse open-consultations-items`} ref={consultationItemContainer}>
            {consultations.map((consultation, index, array) => {
              return (
                <Consultation
                  consultation={consultation}
                  key={consultation.id}
                  isAll={isAll}
                  scrollBarRef={scrollBarRef}
                  array={array}
                  index={index}
                />
              );
            })}
          </div>
        </div>
      )}
      {!isAll && (
        <>
          <ListItemLink
            to={locationDescriptor(`/consultations/${id}/${normalizeString(title)}`)}
            className='open-consultations-item-link content-item-link'
            style={{ zIndex: zIndex ?? (array ? array.length - index : 1) }}
            onClick={(e) => {
              if (rateUpdatesMode && setActiveUpdate) {
                preventLink(e);
                setActiveUpdate({ ...item, importanceRating: feedItemFlags?.[index]?.importanceRating });
                return;
              }
              if (scrollBarRef) {
                rememberPosition('Consultations', scrollBarRef, index);
              }
              if (itemOnclick) {
                itemOnclick(e);
              }
            }}
            tooltipText={tooltipText}
          >
            <div className={`open-consultations-item my-2`}>
              <ListItemContainer
                as='div'
                className='content-item heading collapsed consultation-item adjustment-unseen-dot'
                unseen={unseenConditional}
              >
                <div className='px-4 py-3'>
                  <div className='d-flex align-items-start'>
                    <div className='flex-grow-1 consultation-individual-content-width'>
                      {showContentTypeBadge ? (
                        <div className='mt-1 specific-bottom-margin-12 flex-grow-1 text-light-blue blue-icon content-type-badge'>
                          <ContentTypeBadge objectType={item.type} name={'Consultation'} />
                        </div>
                      ) : (
                        <div className='d-flex align-items-baseline justify-content-between'>
                          <h4 className={`mb-2 content-item-title`}>{title}</h4>
                          {!notShowShare && (
                            <ItemControllerActions
                              rateUpdatesMode={rateUpdatesMode}
                              ratingFeedback={feedItemFlags?.[index]?.importanceRating}
                              priorityBadge={
                                priorityBadgeCondition ? (
                                  <PriorityBadge item={item} feedItemFlags={feedItemFlags[index]} />
                                ) : null
                              }
                              actions={
                                <GeneralItemActions
                                  item={item}
                                  index={index}
                                  thumbDownProps={thumbDownProps}
                                  itemActionsProps={itemActionsProps}
                                />
                              }
                            />
                          )}
                        </div>
                      )}
                      {showContentTypeBadge && <h4 className={`mb-2 content-item-title`}>{title}</h4>}
                      <p className='mb-1 source-name heading-date information-date'>Source: {departmentName}</p>
                      <p
                        className={`heading-date text-grey-description font-weight-normal text-left information-date mb-0`}
                      >
                        {`Opened: ${time(openDate).formatGeneralDate()}`}
                        {item.closeDate !== null && item.closeDate !== undefined && (
                          <>
                            <span className='dot'></span>
                            {closedText(item)}
                          </>
                        )}
                      </p>
                      {((list !== null && activeSearch && includeTags) || embedShowTags) && (
                        <TagsLists
                          feedItemFlags={feedItemFlags}
                          explanations={explanationsToRender}
                          index={index}
                          title={'Consultation'}
                          section={'Consultations'}
                          tagsFixedPosition={tagsFixedPosition}
                          scrollBarRef={scrollBarRef}
                          onlyEmbargoedTag={onlyEmbargoedTag}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </ListItemContainer>
            </div>
          </ListItemLink>
        </>
      )}
    </>
  );
};

const closedText = (item) => {
  let text = '';
  if (calculateDifferenceBetweenDays(item.closeDate) === 0) {
    text = 'Closing today';
  } else if (calculateDifferenceBetweenDays(item.closeDate) < 0) {
    text = 'Closed';
  } else {
    text = `Closing in: ${thousandCommas(calculateDifferenceBetweenDays(item.closeDate))} day${
      calculateDifferenceBetweenDays(item.closeDate) === 1 ? '' : 's'
    }`;
  }
  return <span className={`${calculateDifferenceBetweenDays(item.closeDate) > 7 ? '' : 'below-seven'}`}>{text}</span>;
};

const Consultation = (props) => {
  const { consultation, scrollBarRef, array, index, zIndex } = props;
  const globalState = useContext(store);
  const { state } = globalState;
  const { search } = state;
  const { list } = search;

  const location = useLocation();
  let locationSearch = contentItemSearchQuery(location, list);

  const locationDescriptor = (path) => {
    return {
      pathname: path,
      search: locationSearch,
      hash: getSearchWithinHash() ? getSearchWithinHash(true) : '',
    };
  };
  const { rememberPosition } = useRememberPosition();

  return (
    <Link
      to={locationDescriptor(`/consultations/${consultation.id}/${normalizeString(consultation.title)}`)}
      className='inside-consultation-container position-relative d-block'
      onClick={() => {
        rememberPosition('Consultations', scrollBarRef, index);
      }}
      style={{ zIndex: zIndex ?? (array ? array.length - index : 1) }}
    >
      <div className='pl-4 pr-2 py-3 open-consultation-content' key={consultation.id}>
        <div className='flex-centered mb-3'>
          <div className='flex-grow-1'>
            <p className='open-consultation-link mb-0 flex-grow-1'>{consultation.title}</p>
            <p
              className={`heading-date text-left text-grey-description font-weight-normal text-left open-consultation-text-grey-description font-weight-normal text-left information-date mb-0 mt-1 specific-left-padding-27`}
            >
              <span className='pr-1'>Opened:</span>
              {`${time(consultation.openDate).formatSimpleGeneralDate()}`}
              {consultation.closeDate !== null && consultation.closeDate !== undefined && (
                <>
                  <span className='dot'></span>
                  {closedText(consultation)}
                </>
              )}
            </p>
          </div>
          <Share item={{ ...consultation, contentUrl: consultation.canonicalUrl }} listItem />
        </div>
        <div className='text-grey-description font-weight-normal text-left-container text-lg-right'></div>
      </div>
    </Link>
  );
};

export default ConsultationItem;
